"use client"

import {
  Sources as BaseSources,
  SourcesContent as BaseSourcesContent,
  type SourcesContentProps,
  type SourcesProps,
  type SourcesTriggerProps,
} from "@/components/ai-elements/sources"
import { CollapsibleTrigger } from "@/components/ui/collapsible"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  BookOpenIcon,
  Building2Icon,
  ChevronDownIcon,
  DatabaseIcon,
  GlobeIcon,
  LayersIcon,
  LifeBuoyIcon,
  NotebookPenIcon,
} from "lucide-react"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import type { ComponentProps, MouseEvent, ReactNode } from "react"
import { useState } from "react"

export { BaseSources as Sources, BaseSourcesContent as SourcesContent }
export type { SourcesContentProps, SourcesProps }

export const SourcesTrigger = ({
  className,
  count,
  children,
  ...props
}: SourcesTriggerProps) => {
  const { translate } = useTranslation()

  return (
    <CollapsibleTrigger
      className={cn("group flex items-center gap-1.5", className)}
      {...props}
    >
      {children ?? (
        <>
          <p className="font-medium">
            {translate("AI_ELEMENTS/SOURCES_USED", { count })}
          </p>
          <ChevronDownIcon className="h-4 w-4 origin-center transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </>
      )}
    </CollapsibleTrigger>
  )
}

export type SourceProps = ComponentProps<"a"> & {
  sourceType?:
    | "web"
    | "rag"
    | "mixed"
    | "company_profile"
    | "answering_context"
    | "mc-data"
    | "help"
}

const SourceIcon = ({
  sourceType,
}: {
  sourceType: SourceProps["sourceType"]
}) => {
  switch (sourceType) {
    case "web":
      return <GlobeIcon className="size-3" />
    case "mixed":
      return <LayersIcon className="size-3" />
    case "company_profile":
      return <Building2Icon className="size-3" />
    case "answering_context":
      return <NotebookPenIcon className="size-3" />
    case "mc-data":
      return <DatabaseIcon className="size-3" />
    case "help":
      return <LifeBuoyIcon className="size-3" />
    default:
      return <BookOpenIcon className="size-3" />
  }
}

export const Source = ({
  href,
  title,
  children,
  sourceType = "rag",
  ...props
}: SourceProps) => {
  const content = children ?? (
    <>
      <SourceIcon sourceType={sourceType} />
      <span className="block font-medium">{title}</span>
    </>
  )

  // Non-link variant: when href is omitted, render a static span. Used for
  // sources that aren't navigable (e.g. the company profile).
  if (href === undefined) {
    const { onClick: _onClick, className, ...spanProps } = props
    return (
      <span
        className={cn("flex items-center gap-2", className)}
        {...(spanProps as ComponentProps<"span">)}
      >
        {content}
      </span>
    )
  }

  const { className, ...anchorProps } = props
  return (
    <a
      className={cn("flex items-center gap-2", className)}
      href={href}
      rel="noreferrer"
      target="_blank"
      {...anchorProps}
    >
      {content}
    </a>
  )
}

const getDomain = (url?: string) => {
  if (!url) return undefined

  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return undefined
  }
}

export type SourceItem = {
  key: string
  title: string
  subtitle?: string
  sourceType: "web" | "rag" | "mc-data" | "help"
  url?: string
  snippet?: string
  onClick?: (event: MouseEvent<HTMLElement>) => void
}

const SourcePill = ({ item }: { item: SourceItem }) => {
  const { translate } = useTranslation()
  const domain = getDomain(item.url)
  const meta =
    item.sourceType === "web"
      ? (domain ?? "external")
      : item.sourceType === "mc-data"
        ? translate("AI_CHAT/SOURCES/MC_LABEL")
        : item.sourceType === "help"
          ? translate("AI_CHAT/SOURCES/HELP_LABEL")
          : item.subtitle

  const content = (
    <>
      <span className="flex size-3.5 shrink-0 items-center justify-center text-primary/80 transition-colors group-hover/pill:text-primary">
        {item.sourceType === "web" ? (
          <GlobeIcon className="size-3" />
        ) : item.sourceType === "mc-data" ? (
          <DatabaseIcon className="size-3" />
        ) : item.sourceType === "help" ? (
          <LifeBuoyIcon className="size-3" />
        ) : (
          <BookOpenIcon className="size-3" />
        )}
      </span>
      <span className="max-w-[180px] truncate font-medium text-foreground">
        {item.title}
      </span>
      {meta && <span className="shrink-0 text-muted-foreground">· {meta}</span>}
    </>
  )

  const baseClassName =
    "group/pill inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full bg-grey1/35 px-2.5 py-1 text-left text-[11px] backdrop-blur-xs"

  // Sources without an action (e.g. mc-data — no backing document) render as
  // a static badge, not a focusable button that promises interaction.
  if (!item.onClick) {
    return <span className={baseClassName}>{content}</span>
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      className={`${baseClassName} transition-all duration-150 hover:-translate-y-px hover:bg-grey1/60 hover:shadow-[0_6px_18px_-10px_hsl(var(--primary)/0.4)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {content}
    </button>
  )
}

const SourceListRow = ({ item }: { item: SourceItem }) => {
  const { translate } = useTranslation()
  const domain = getDomain(item.url)
  const meta =
    item.sourceType === "web"
      ? (domain ?? "external")
      : item.sourceType === "mc-data"
        ? translate("AI_CHAT/SOURCES/MC_LABEL")
        : item.sourceType === "help"
          ? translate("AI_CHAT/SOURCES/HELP_LABEL")
          : item.subtitle

  const content = (
    <>
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm text-primary/80 transition-colors group-hover/row:text-primary">
        {item.sourceType === "web" ? (
          <GlobeIcon className="size-3.5" />
        ) : item.sourceType === "mc-data" ? (
          <DatabaseIcon className="size-3.5" />
        ) : item.sourceType === "help" ? (
          <LifeBuoyIcon className="size-3.5" />
        ) : (
          <BookOpenIcon className="size-3.5" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-2">
          <p className="truncate text-[13px] font-semibold text-foreground">
            {item.title}
          </p>
          {meta && (
            <p className="shrink-0 text-[11px] text-muted-foreground">
              · {meta}
            </p>
          )}
        </div>
        {item.snippet && (
          <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">
            {item.snippet}
          </p>
        )}
      </div>
    </>
  )

  const baseClassName =
    "group/row flex w-full items-start gap-3 rounded-lg bg-grey1/30 px-3 py-2 text-left backdrop-blur-xs"

  // Static variant for sources without an action (e.g. mc-data).
  if (!item.onClick) {
    return <div className={baseClassName}>{content}</div>
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      className={`${baseClassName} transition-all duration-150 hover:bg-grey1/55 hover:shadow-[0_6px_18px_-10px_hsl(var(--primary)/0.35)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring`}
    >
      {content}
    </button>
  )
}

export type SourceCardRowProps = {
  items: SourceItem[]
  className?: string
  align?: "start" | "end"
}

export const SourceCardRow = ({
  items,
  className,
  align = "start",
}: SourceCardRowProps) => {
  const [open, setOpen] = useState(false)
  const { translate } = useTranslation()

  if (!items.length) return null

  const hasSnippets = items.some((it) => it.snippet)
  const detailLabel = open
    ? translate("AI_ELEMENTS/HIDE_DETAILS")
    : translate("AI_ELEMENTS/SHOW_DETAILS")

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-1.5",
          align === "end" && "justify-end",
        )}
      >
        {items.map((item) => (
          <SourcePill key={item.key} item={item} />
        ))}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <m.div
            key="source-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-1.5 pt-1">
              {items.map((item) => (
                <SourceListRow key={item.key} item={item} />
              ))}
              {!hasSnippets && (
                <p className="px-3 text-[10px] italic text-muted-foreground/70">
                  {translate("AI_ELEMENTS/NO_SNIPPETS")}
                </p>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export type SourceCardProps = SourceItem & {
  className?: string
}

/** @deprecated Use SourceCardRow with items prop. Kept for backward compat. */
export const SourceCard = ({ className, ...item }: SourceCardProps) => (
  <div className={className}>
    <SourcePill item={item} />
  </div>
)

export const _SourcePill = SourcePill
export const _SourceListRow = SourceListRow

export type SourceCardRowChildren = {
  children?: ReactNode
}
